import {customFieldToFilterType} from '@/components/query/custom-fields';
import auth from '@/common/auth/auth';
import CachedColumn from './cached-column';
import PreferenceTypes from '@/store/preference-types';

export default class ColumnSet {
    constructor({
        id,
        store,
        batchActions = [],
        actionColumns = [],
        defaultColumns = [],
        otherColumns = [],
        lockedColumns = [],
        segmentDefaultColumnIds = [],
        customFields = null,
        version = null,
    }) {
        this.id = id;
        this.store = store;
        this.version = version;
        this.batchActions = [...batchActions];
        this.actionColumns = [...actionColumns];
        this.defaultColumns = [...defaultColumns];
        this.otherColumns = [...otherColumns];
        this.lockedColumns = [...lockedColumns];
        this.customFields = customFields;
        this.preCacheColumns();
        // initial state of selected columns
        // until they are explicitly requested by the grid
        this.segmentDefaultColumnIds = segmentDefaultColumnIds;
    }

    // Get and Set the selected column IDs and column widths

    /**
     * Returns an array of column IDs (generated by column caching) that
     * represents the selected columns in the grid.
     * @returns {string[]}
     */
    getSelectedColumnIds() {
        let columnIds;

        // attempt to load from preferences
        const idsFromPrefs = (() => {
            const columnPreferences = this.store.getters['user/getUserPreference'](PreferenceTypes.columns, this.id);
            if (columnPreferences && columnPreferences.columns) {
                const {columns, version: preferenceVersion = null} = columnPreferences;
                if (!this.preferencesOutOfDate(preferenceVersion)) {
                    return [...columns];
                }
            }
            return null;
        })();

        if (idsFromPrefs && idsFromPrefs.length) {
            columnIds = idsFromPrefs;
        } else {
            columnIds = this.getDefaultColumnIds();
        }

        // ensure sticky columns are always present and first in the list
        const stickyAndSelectedColumnIds = [
            ...new Set([...this.getStickyColumnIds(), ...columnIds]),
        ];

        // filter out columns that aren't selectable
        const selectableColumns = this.getSelectableColumns();
        return stickyAndSelectedColumnIds.filter(id => Reflect.has(selectableColumns, id));
    }

    /**
     * Set the selected columns to the desired list of IDs.
     * @param {array} columns
     */
    setSelectedColumnIds(columnsToSave) {
        const selectableColumns = this.getSelectableColumns();
        // filter out columns that aren't selectable
        const columns = columnsToSave.filter(id => Reflect.has(selectableColumns, id));
        this.saveToPreferences({columns});
    }

    /**
     * Returns an object of column widths, with keys matched to selected column IDs
     * @returns {Object}
     */
    getColumnWidths() {
        const widthsFromPrefs = (() => {
            const columnPreferences = this.store.getters['user/getUserPreference'](PreferenceTypes.columns, this.id);
            if (columnPreferences && columnPreferences.widths) {
                const {widths, version: preferenceVersion = null} = columnPreferences;
                if (!this.preferencesOutOfDate(preferenceVersion)) {
                    return {...widths};
                }
            }
            return {};
        })();

        // map the column widths to the currently selected IDs
        // add preceding actionColumns so that resize manager works properly
        return [...Object.keys(this.actionColumns), ...this.getSelectedColumnIds()]
            .map(id => widthsFromPrefs[id] || null);
    }

    /**
     * Saves the column widths of the passed in columns.
     * If widths submitted is empty, clear all saved widths
     * @param {array} widths Array of widths
     */
    setColumnWidths(widthsToSave = []) {
        const widths = {};
        const widthSettingsPresent = widthsToSave.some(entry => entry);
        const selectedColumnIds = this.getSelectedColumnIds();
        if (widthSettingsPresent) {
            this.getColumnsToRender().forEach((column) => {
                const widthToSave = widthsToSave.shift() || null;
                if (selectedColumnIds.includes(column.id)) {
                    widths[column.id] = widthToSave;
                }
            });
        }
        this.saveToPreferences({widths});
    }

    // Various getters for different column concerns

    /**
     * Returns an array of columns that
     * represents the selected columns in the grid.
     * @returns {[CachedColumn]}
     */
    getSelectedColumns() {
        const selectableColumns = this.getSelectableColumns();
        return this.getSelectedColumnIds()
            .map(id => selectableColumns[id])
            .filter(column => column);
    }

    /**
     * Returns an object listing all columns available for the user to select/rearrange
     * @returns {{CachedColumn}}
     */
    getSelectableColumns() {
        let columns = {
            ...this.defaultColumns,
            ...this.otherColumns,
        };
        if (auth.customFields.read.isAllowed) {
            const customFieldsColumns = this.getCustomFieldsColumns();
            if (customFieldsColumns) {
                columns = {
                    ...columns,
                    ...customFieldsColumns,
                };
            }
        }
        return columns;
    }

    /**
     * Parses the custom fields for the resource type defined in the column set
     * and generates a list of cached columns from it.
     * @return {null|Object}
     */
    getCustomFieldsColumns() {
        if (!this.customFields) {
            return null;
        }
        const customFields = this.store.getters['rulesEngine/customFields'](this.customFields);
        const reducer = (result, {fields}) => {
            const type = fields.type === 'boolean'
                ? 'text' // TODO fix special case
                : fields.type;
            const column = new CachedColumn({
                ...fields,
                value: givenFields => ('customFields' in givenFields ? givenFields.customFields[fields.name] : null),
                type: customFieldToFilterType(type),
            });
            return {
                ...result,
                [column.id]: column,
            };
        };
        return customFields
            ? customFields.fields.reduce(reducer, {})
            : null;
    }

    /**
     * Returns an array of all columns to render
     * @returns {[CachedColumn]}
     */
    getColumnsToRender() {
        return [...Object.values(this.actionColumns),
            ...this.getSelectedColumns(),
            ...Object.values(this.lockedColumns)];
    }

    /**
     * Returns an array ids of all sticky columns
     * @returns {[String]}
     */
    getStickyColumnIds() {
        return Object.values(this.getSelectableColumns())
            .filter(column => column.sticky)
            .map(({id}) => id);
    }

    /**
     * Returns an array ids of all default columns
     * @returns {[String]}
     */
    getDefaultColumnIds() {
        // check if defaults defined by hydration, usually for segments
        if (this.segmentDefaultColumnIds.length) {
            return this.segmentDefaultColumnIds;
        }
        return Object.keys(this.defaultColumns);
    }

    // Helper methods

    /**
     * Checks the remote preference version against the current one
     * In case we have manual UI updates
     * @returns {boolean}
     */
    preferencesOutOfDate(preferenceVersion) {
        if (this.version !== null
            && this.version > preferenceVersion) {
            return true;
        }
        return false;
    }

    /**
     * Save the selected columns to the preference store and trigger
     * an API update to the profile endpoint.
     * @returns {Promise<void>}
     */
    async saveToPreferences({widths = null, columns = null}) {
        const data = {
            version: this.version,
        };
        if (widths) {
            data.widths = widths;
        }
        if (columns) {
            data.columns = columns;
        }
        this.store.dispatch('user/setPartialUserPreference', {
            type: PreferenceTypes.columns,
            id: this.id,
            data,
        });
    }

    /**
     * Transform the original column definitions into CachedColumns for both
     * the default grid columns, and everything else available to the user.
     */
    preCacheColumns() {
        this.actionColumns = this.actionColumns.reduce((result, definition) => {
            const column = new CachedColumn({
                ...definition,
                actionColumn: true,
            });
            return {
                ...result,
                [column.id]: column,
            };
        }, {});
        this.defaultColumns = this.defaultColumns.reduce((result, definition) => {
            const column = new CachedColumn({
                ...definition,
                defaultColumn: true,
            });
            return {
                ...result,
                [column.id]: column,
            };
        }, {});
        this.otherColumns = this.otherColumns.reduce((result, definition) => {
            const column = new CachedColumn(definition);
            return {
                ...result,
                [column.id]: column,
            };
        }, {});
        this.lockedColumns = this.lockedColumns.reduce((result, definition) => {
            const column = new CachedColumn({
                ...definition,
                lockedColumn: true,
            });
            return {
                ...result,
                [column.id]: column,
            };
        }, {});
    }
}
